// Address search over the static index/addr.json of each region (GURS house numbers).

import { fetchJson } from './http';
import { REGIONS, regionUrl } from './region';

interface AddrFile { v: number; places: string[]; streets: string[]; a: [number, number, string, number, number][] }

export interface Address { label: string; E: number; N: number; region: string }
interface Entry extends Address { tokens: string[]; hs: string }

/** Lowercase, strip diacritics (č → c), keep letters, digits and spaces. */
export const fold = (s: string) => s.toLocaleLowerCase('sl').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();

let entries: Promise<Entry[]> | null = null;

export function loadAddresses(): Promise<Entry[]> {
  entries ??= Promise.all(REGIONS.map(async (r) => {
    const f = await fetchJson<AddrFile>(regionUrl(r.id, 'index/addr.json')).catch(() => null);
    if (!f) return [];
    return f.a.map(([pi, si, hs, E, N]) => {
      const place = f.places[pi], street = si >= 0 ? f.streets[si] : null;
      const label = street ? `${street} ${hs}, ${place}` : `${place} ${hs}`;
      return { label, E, N, region: r.id, hs, tokens: fold(label).split(' ') };
    });
  })).then((a) => a.flat());
  entries.catch(() => { entries = null; });
  return entries;
}

/** Every query token must prefix a token of the address; exact house-number matches rank first. */
export function searchAddresses(all: Entry[], q: string, limit = 8): Address[] {
  const qt = fold(q).split(' ').filter(Boolean);
  if (!qt.length || (qt.length === 1 && qt[0].length < 2)) return [];
  // "28 b" → "28b"
  for (let i = qt.length - 1; i > 0; i--) if (/^\d+$/.test(qt[i - 1]) && /^[a-z]$/.test(qt[i])) { qt[i - 1] += qt[i]; qt.splice(i, 1); }
  const scored: [number, Entry][] = [];
  for (const e of all) {
    let score = 0, ok = true;
    for (const t of qt) {
      let best = 0;
      for (const w of e.tokens) {
        if (w === t) { best = 2; break; }
        if (w.startsWith(t)) best = 1;
      }
      if (!best) { ok = false; break; }
      score += best + (best === 2 && t === e.hs ? 2 : 0);
    }
    if (ok) scored.push([score, e]);
  }
  scored.sort((a, b) => b[0] - a[0]);
  return scored.slice(0, limit).map(([, e]) => ({ label: e.label, E: e.E, N: e.N, region: e.region }));
}
